import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { t } from 'ember-intl';
import { PhMagnifyingGlass } from 'ember-phosphor-icons';
import Heading from 'tio-ui/components/heading';
import ReportStatsModal from '../report-stats-modal';
import PartnerReportRow from './report-row';
import IconBadge from 'tio-ui/components/icon-badge';
import { PRODUCT_BADGES } from '../reports/product-icon-badge';
import {
  PRODUCT_LABELS,
  PRODUCT_SECTION_ORDER,
  VALID_REPORT_TYPES,
  type ProductCode,
} from '../../utils/dataset-utils';
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import { query } from '@warp-drive/utilities/json-api';

/** One server page. Search filters only what was fetched, so rows beyond this are unreachable. */
const REPORT_PAGE_LIMIT = 200;

/** Strategy Finder is not a paid product; the admin builder's pickers still distinguish it. */
const PARTNER_PRODUCT_ALIASES: Partial<Record<ProductCode, ProductCode>> = {
  STRATEGYFINDER: 'Core',
};

/** The shared PRODUCT_LABELS are the admin builder's; SYF stays "Secure" per employee-facing copy. */
const PARTNER_PRODUCT_LABELS: Partial<Record<ProductCode, string>> = {
  PSLF: 'Public Service Loan Forgiveness',
  SLR: 'Student Loan Repayment',
  Core: 'Core Platform',
};

interface PartnerConsolidatedReportsPageSignature {
  Args: {
    companyId: string;
    viewerRoute: string;
    /** Lowercase product codes to list; omit for every builder product. */
    reportTypes?: string[];
    /** Live search text, owned by the route template so the input can sit in PageHeader's :toolbar. */
    searchQuery?: string;
  };
}

export default class PartnerConsolidatedReportsPage extends Component<PartnerConsolidatedReportsPageSignature> {
  @service declare store: StoreService;

  /** Report whose usage-stats modal is open (null = closed) */
  @tracked statsReport: Report | null = null;

  openStats = (report: Report) => {
    this.statsReport = report;
  };

  closeStats = () => {
    this.statsReport = null;
  };

  /** FAIL CLOSED: only an OMITTED arg means all products; an empty list allows nothing. */
  get effectiveReportTypes(): string[] {
    const requested = this.args.reportTypes;
    if (requested === undefined) return [...VALID_REPORT_TYPES];
    return [
      ...new Set(
        requested.map((type) => type.toLowerCase()).filter((type) => VALID_REPORT_TYPES.has(type))
      ),
    ];
  }

  reportsData = trackedFunction(this, async () => {
    if (this.effectiveReportTypes.length === 0) return { rows: [], total: 0 };

    const { content } = (await this.store.request(
      query<Report>(
        'report',
        {
          'page[limit]': REPORT_PAGE_LIMIT,
          'filter[report_type]': this.effectiveReportTypes.join(','),
        },
        { namespace: 'api/v2' }
      )
    )) as { content: { data: Report[]; meta?: { page?: { total?: number } } } };
    // Absent meta reads as "no more than we got", so the notice stays off rather than guessing.
    return { rows: content.data, total: content.meta?.page?.total ?? content.data.length };
  });

  get isLoading() {
    return this.reportsData.isLoading;
  }

  /** A rejected request leaves `value` empty, which would otherwise read as a company with no reports. */
  get loadFailed() {
    return Boolean(this.reportsData.error);
  }

  get isTruncated() {
    const loaded = this.reportsData.value;
    return Boolean(loaded && loaded.total > loaded.rows.length);
  }

  reportPageLimit = REPORT_PAGE_LIMIT;

  /** Placeholder rows while the listing loads; the count is cosmetic. */
  skeletonRows = [1, 2, 3];

  /** Published, non-archived reports within the allowed types. */
  get publishedReports() {
    const all = this.reportsData.value?.rows ?? [];
    const allowedTypes = this.effectiveReportTypes;
    // No visibility filter: ReportPolicy::Scope already narrowed this to the viewer's company.
    return all.filter(
      (r) =>
        allowedTypes.includes((r.reportType || '').toLowerCase()) && r.isPublished && !r.isArchived
    );
  }

  get trimmedQuery() {
    return (this.args.searchQuery ?? '').trim().toLowerCase();
  }

  get hasQuery() {
    return this.trimmedQuery.length > 0;
  }

  /** Matches name and description, the two fields a partner admin can actually see on a row. */
  get matchingReports() {
    const needle = this.trimmedQuery;
    if (!needle) return this.publishedReports;
    return this.publishedReports.filter(
      (report) =>
        (report.name || '').toLowerCase().includes(needle) ||
        (report.description || '').toLowerCase().includes(needle)
    );
  }

  /** Groups in the fixed product order; a product with no matches is omitted entirely. */
  get reportsByProduct() {
    const grouped = new Map<ProductCode, Report[]>();
    for (const report of this.matchingReports) {
      const rawType = (report.reportType || '').toUpperCase() as ProductCode;
      const known = PRODUCT_LABELS[rawType] ? rawType : 'Core';
      const product = PARTNER_PRODUCT_ALIASES[known] ?? known;
      const list = grouped.get(product) ?? [];
      list.push(report);
      grouped.set(product, list);
    }
    return PRODUCT_SECTION_ORDER.filter((code) => grouped.has(code)).map((code) => {
      // Badge the resolved group, never a report: an aliased one carries its own product.
      return {
        product: code,
        label: PARTNER_PRODUCT_LABELS[code] ?? PRODUCT_LABELS[code],
        reports: grouped.get(code) ?? [],
        badge: PRODUCT_BADGES[code],
      };
    });
  }

  get matchCount() {
    return this.matchingReports.length;
  }

  /** A query that filtered everything out — distinct from a company with no reports at all. */
  get hasNoMatches() {
    return this.hasQuery && this.matchCount === 0;
  }

  queryFor = (report: Report) => {
    return {
      reportId: report.id,
      companyId: this.args.companyId,
    };
  };

  <template>
    {{#if this.isLoading}}
      <div class="tio-col-loose" aria-busy="true" data-test-partner-reports-skeleton>
        <span class="sr-only">{{t "reporting.loading"}}</span>
        {{#each this.skeletonRows}}
          <div class="tio-row rounded-xl border tio-border-default tio-bg-surface px-4 py-3">
            <div class="h-8 w-8 shrink-0 rounded-lg tio-bg-sunken"></div>
            <div class="tio-col-extra-tight min-w-0 flex-1">
              <div class="h-4 w-1/3 rounded tio-bg-sunken"></div>
              <div class="h-3 w-2/3 rounded tio-bg-sunken"></div>
            </div>
          </div>
        {{/each}}
      </div>
    {{else if this.loadFailed}}
      <div class="py-8 text-center" role="alert" data-test-partner-reports-error>
        <p class="tio-fg-danger">{{t "reporting.load_error"}}</p>
      </div>
    {{else if this.publishedReports.length}}
      <div class="tio-col-loose">
        {{#if this.hasNoMatches}}
          <div
            class="rounded-2xl border border-dashed tio-border-strong tio-bg-surface px-6 py-10 text-center"
            data-test-partner-reports-no-matches
          >
            <PhMagnifyingGlass class="mx-auto h-7 w-7 tio-fg-muted" />
            <p class="tio-text-base-semibold mt-3 tio-fg-primary">
              {{t "reporting.no_match_title" query=@searchQuery}}
            </p>
            <p class="tio-text-sm-normal mt-1 tio-fg-secondary">{{t "reporting.no_match_hint"}}</p>
          </div>
        {{else}}
          <section data-test-partner-reports-detailed>
            <div class="tio-row-tight">
              <Heading @level={{2}} @role="section-title">
                {{t "reporting.detailed_reports"}}
              </Heading>
              <span
                class="tio-text-xs-semibold rounded-full tio-bg-sunken px-2 py-1 tio-fg-muted"
                data-test-partner-reports-count
              >{{this.matchCount}}</span>
            </div>
            <p class="tio-text-sm-normal mt-1 tio-fg-secondary">
              {{t "reporting.detailed_reports_subtitle"}}
            </p>

            <div class="tio-col-loose mt-4">
              {{#each this.reportsByProduct as |section|}}
                <div class="tio-col-tight" data-test-partner-reports-group={{section.product}}>
                  <div class="tio-row-tight">
                    <IconBadge
                      @icon={{section.badge.icon}}
                      @color={{section.badge.color}}
                      @size="sm"
                      class="shrink-0"
                    />
                    <Heading @level={{3}} @role="panel-title">{{section.label}}</Heading>
                    <span class="tio-text-xs-normal tio-fg-muted">
                      {{t "reporting.report_count" count=section.reports.length}}
                    </span>
                  </div>

                  {{#each section.reports as |report|}}
                    <PartnerReportRow
                      @report={{report}}
                      @route={{@viewerRoute}}
                      @query={{this.queryFor report}}
                      @onStats={{this.openStats}}
                    />
                  {{/each}}
                </div>
              {{/each}}
            </div>
          </section>
        {{/if}}
      </div>
    {{else}}
      <div class="py-8 text-center">
        <p class="tio-fg-muted">{{t "reporting.no_reports"}}</p>
      </div>
    {{/if}}

    {{#if this.isTruncated}}
      <p
        class="tio-text-xs-normal tio-fg-muted py-2 text-center"
        data-test-partner-reports-truncated
      >{{t "reporting.truncated_notice" limit=this.reportPageLimit}}</p>
    {{/if}}

    <ReportStatsModal
      @report={{this.statsReport}}
      @companyId={{@companyId}}
      @onClose={{this.closeStats}}
    />
  </template>
}
